import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    setMenuOpen(false);
    if (!isHomePage) {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-black/95 border-b border-white/10' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://public.readdy.ai/ai/img_res/2935092e-5ec4-485e-b1db-609b8c1d9eff.png"
                alt="GWDevelopers Logo"
                className="h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                How It Works
              </button>
              <Link
                to="/portfolio"
                className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium whitespace-nowrap"
              >
                Portfolio
              </Link>
              <button
                onClick={() => scrollToSection('pricing')}
                className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                Pricing
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-6 py-2.5 border border-white/80 rounded-full text-white text-sm font-medium hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Request Preview
              </button>
            </div>

            {/* Hamburger Button */}
            <button
              className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer z-50"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span
                className={`block w-6 h-0.5 bg-white transition-all duration-300 ${
                  menuOpen ? 'rotate-45 translate-y-2' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-white transition-all duration-300 ${
                  menuOpen ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-white transition-all duration-300 ${
                  menuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 transition-opacity duration-300 md:hidden ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 z-40 bg-black border-l border-white/10 flex flex-col pt-24 pb-10 px-8 gap-6 transition-transform duration-300 md:hidden ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <button
          onClick={() => scrollToSection('services')}
          className="text-gray-300 hover:text-white transition-colors duration-300 text-base font-medium cursor-pointer whitespace-nowrap text-left"
        >
          Services
        </button>
        <button
          onClick={() => scrollToSection('how-it-works')}
          className="text-gray-300 hover:text-white transition-colors duration-300 text-base font-medium cursor-pointer whitespace-nowrap text-left"
        >
          How It Works
        </button>
        <Link
          to="/portfolio"
          onClick={() => setMenuOpen(false)}
          className="text-gray-300 hover:text-white transition-colors duration-300 text-base font-medium whitespace-nowrap"
        >
          Portfolio
        </Link>
        <button
          onClick={() => scrollToSection('pricing')}
          className="text-gray-300 hover:text-white transition-colors duration-300 text-base font-medium cursor-pointer whitespace-nowrap text-left"
        >
          Pricing
        </button>
        <button
          onClick={() => scrollToSection('about')}
          className="text-gray-300 hover:text-white transition-colors duration-300 text-base font-medium cursor-pointer whitespace-nowrap text-left"
        >
          About
        </button>
        <div className="mt-4">
          <button
            onClick={() => scrollToSection('get-started')}
            className="w-full px-6 py-3 border border-white/80 rounded-full text-white text-sm font-medium hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Request Preview
          </button>
        </div>
      </div>
    </>
  );
}
